import { createRouter, createWebHistory } from 'vue-router'
import { clearToken, getStoredToken, hasRequiredScope, hasValidToken, hasStaffSession } from '@/services/auth'

import LoginView from '@/views/LoginView.vue'
import BankIdAuthView from '@/views/BankIdAuthView.vue'
import ApplicationsView from '@/views/ApplicationsView.vue'
import ApplicationDraftsView from '@/views/create-application/ApplicationDraftsView.vue'
import CreateApplication from '@/views/create-application/ansokan/CreateApplication.vue'
import DebtLayout from '@/views/debt/DebtLayout.vue'
import DebtOverviewView from '@/views/debt/overview/DebtOverviewView.vue'
import DebtDocumentsView from '@/views/debt/documents/DebtDocumentsView.vue'
import DepositLoanLayout from '@/views/deposit-loan/DepositLoanLayout.vue'
import UserLayout from '@/views/user/UserLayout.vue'
import UserProfile from '@/views/user/UserProfile.vue'
import UserDocumentsView from '@/views/user/documents/UserDocumentsView.vue'
import PersonnelView from '@/views/personnel/PersonnelView.vue'
import PricingView from '@/views/pricing/PricingView.vue'
import EventsView from '@/views/events/EventsView.vue'
import ApplicationLayout from '@/views/application/ApplicationLayout.vue'
import ApplicationOverview from '@/views/application/overview/ApplicationOverview.vue'
import ApplicationDocumentsView from '@/views/application/documents/ApplicationDocumentsView.vue'
import ApplicationAdditionsView from '@/views/application/application-additions/ApplicationAdditionsView.vue'
import CommitmentLayout from '@/views/commitment/CommitmentLayout.vue'
import CommitmentOverview from '@/views/commitment/CommitmentOverview.vue'
import CreateCommitmentApplication from '@/views/create-application/lofte/CreateCommitmentApplication.vue'
import DesignSystem from '@/views/design-system/DesignSystem.vue'

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/',
      redirect: '/login',
    },
    {
      // Internal brand book. No auth and intentionally not linked from any nav —
      // reachable only by typing the URL. Kept auth-free so it can be opened directly
      path: '/design-system',
      name: 'design-system',
      component: DesignSystem,
    },
    {
      path: '/login',
      name: 'login',
      component: LoginView,
    },
    {
      path: '/auth/:orderRef',
      name: 'auth',
      component: BankIdAuthView,
    },
    {
      path: '/ansokningar',
      name: 'ansokningar',
      component: ApplicationsView,
      meta: { requiresAuth: true },
    },
    {
      path: '/ansokningar/:id',
      component: ApplicationLayout,
      meta: { requiresAuth: true },
      children: [
        { path: '', name: 'ansokan', component: ApplicationOverview },
        { path: 'dokument', name: 'ansokan-dokument', component: ApplicationDocumentsView },
        { path: 'kompletteringar', name: 'ansokan-kompletteringar', component: ApplicationAdditionsView },
      ],
    },
    {
      path: '/lofte/:id',
      component: CommitmentLayout,
      meta: { requiresAuth: true },
      children: [
        { path: '', name: 'lofte', component: CommitmentOverview },
        { path: 'dokument', name: 'lofte-dokument', component: ApplicationDocumentsView },
      ],
    },
    {
      path: '/ny-ansokan',
      name: 'ny-ansokan',
      component: ApplicationDraftsView,
      meta: { requiresAuth: true },
    },
    {
      path: '/ny-ansokan/:id',
      name: 'ny-ansokan-utkast',
      component: CreateApplication,
      meta: { requiresAuth: true },
    },
    {
      path: '/nytt-lofte/:id',
      name: 'nytt-lofte-utkast',
      component: CreateCommitmentApplication,
      meta: { requiresAuth: true },
    },
    {
      path: '/personal',
      name: 'personal',
      component: PersonnelView,
      meta: { requiresAuth: true },
    },
    {
      path: '/rantor',
      name: 'rantor',
      component: PricingView,
      meta: { requiresAuth: true },
    },
    {
      path: '/handelser',
      name: 'handelser',
      component: EventsView,
      meta: { requiresAuth: true },
    },
    {
      path: '/bolan/:debt_id',
      component: DebtLayout,
      meta: { requiresAuth: true },
      children: [
        { path: '', name: 'bolan', component: DebtOverviewView },
        { path: 'dokument', name: 'bolan-dokument', component: DebtDocumentsView },
      ],
    },
    {
      path: '/kunder/:id',
      component: UserLayout,
      meta: { requiresAuth: true },
      children: [
        { path: '', name: 'kund', component: UserProfile },
        { path: 'dokument', name: 'kund-dokument', component: UserDocumentsView },
      ],
    },
    {
      path: '/handpenningslan/:id',
      name: 'handpenningslan',
      component: DepositLoanLayout,
      meta: { requiresAuth: true },
    },
  ],
})

router.beforeEach((to) => {
  const token = getStoredToken()
  const hasToken = Boolean(token)
  const isValid = hasValidToken()
  const hasScope = hasRequiredScope('broker')

  if (to.meta.requiresAuth) {
    if (!hasToken) {
      return { path: '/login', query: { redirect: to.fullPath, reason: 'missing_token' } }
    }

    if (!isValid) {
      clearToken()
      return { path: '/login', query: { redirect: to.fullPath, reason: 'token_expired' } }
    }

    if (!hasScope) {
      clearToken()
      return { path: '/login', query: { redirect: to.fullPath, reason: 'invalid_scope' } }
    }
  }

  if (to.path === '/login' && hasStaffSession()) {
    return { path: '/ansokningar' }
  }

  return true
})

export default router
